import { Component } from '@theme/component';

const HOVER_DELAY = 80;
const LEAVE_DELAY = 250;

/**
 * A custom element that manages nested mega menu hover interactions.
 * Handles showing/hiding child menus when hovering over parent menu items.
 *
 * @extends {Component}
 */
class MegaMenuNested extends Component {
  /** @type {HTMLElement | null} */
  #activeParent = null;
  /** @type {number | null} */
  #hoverTimeout = null;
  /** @type {number | null} */
  #leaveTimeout = null;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#initializeHoverListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearTimeouts();
  }

  /**
   * Initialize hover listeners for desktop mega menu items
   */
  #initializeHoverListeners() {
    const parentLinks = this.querySelectorAll('.mega-menu__link--parent:not(.mega-menu__link--button)');

    parentLinks.forEach((link) => {
      if (!(link instanceof HTMLElement)) return;

      const linkContainer = link.parentElement;
      if (!linkContainer) return;

      const childMenu = linkContainer.querySelector('ul');
      if (!(childMenu instanceof HTMLElement)) return;

      childMenu.setAttribute('aria-hidden', 'true');
      link.setAttribute('aria-expanded', 'false');

      link.addEventListener(
        'mouseenter',
        /** @param {MouseEvent} event */
        (event) => {
          this.#handleParentEnter(event, link, childMenu);
        },
        { signal: this.#abortController.signal }
      );

      linkContainer.addEventListener(
        'mouseleave',
        /** @param {MouseEvent} event */
        (event) => {
          this.#handleParentLeave(event, link, childMenu);
        },
        { signal: this.#abortController.signal }
      );
    });
  }

  /**
   * Handle mouse enter on parent link
   * @param {MouseEvent} event
   * @param {HTMLElement} link
   * @param {HTMLElement} childMenu
   */
  #handleParentEnter(event, link, childMenu) {
    if (this.#leaveTimeout) {
      clearTimeout(this.#leaveTimeout);
      this.#leaveTimeout = null;
    }

    if (this.#activeParent && this.#activeParent !== link) {
      const previousContainer = this.#activeParent.parentElement;
      const previousChildMenu = previousContainer?.querySelector('ul');
      if (previousChildMenu) {
        this.#activeParent.setAttribute('aria-expanded', 'false');
        previousChildMenu.setAttribute('aria-hidden', 'true');
      }
    }

    this.#hoverTimeout = setTimeout(() => {
      childMenu.setAttribute('aria-hidden', 'false');
      link.setAttribute('aria-expanded', 'true');
      this.#activeParent = link;
    }, HOVER_DELAY);
  }

  /**
   * Handle mouse leave from parent container
   * @param {MouseEvent} event
   * @param {HTMLElement} link
   * @param {HTMLElement} childMenu
   */
  #handleParentLeave(event, link, childMenu) {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }

    this.#leaveTimeout = setTimeout(() => {
      link.setAttribute('aria-expanded', 'false');
      childMenu.setAttribute('aria-hidden', 'true');

      if (this.#activeParent === link) {
        this.#activeParent = null;
      }
    }, LEAVE_DELAY);
  }

  /**
   * Clear all timeouts
   */
  #clearTimeouts() {
    if (this.#hoverTimeout) {
      clearTimeout(this.#hoverTimeout);
      this.#hoverTimeout = null;
    }
    if (this.#leaveTimeout) {
      clearTimeout(this.#leaveTimeout);
      this.#leaveTimeout = null;
    }
  }
}

if (!customElements.get('mega-menu-nested')) {
  customElements.define('mega-menu-nested', MegaMenuNested);
}

